<template>
    <div>
        <v-card flat class="shadow mt-3">
            <!-- Header Section -->
            <div>
                <v-row align="center" class="pa-3">
                    <v-col
                        cols="12"
                        md="2"
                        class="mb-sm-1"
                        v-if="
                            hasRole([
                                Roles.AdminCreate,
                                Roles.Admin,
                                Roles.Administrator,
                                Roles.SuperAdmin,
                            ])
                        "
                    >
                        <v-btn
                            @click="openAddDialog()"
                            color="primary"
                            class="btn"
                        >
                            <v-icon small class="pl-1">mdi-plus</v-icon>
                            مشرف جديد
                        </v-btn>
                    </v-col>
                    <v-col cols="12" md="3" class="mb-sm-1">
                        <div class="d-flex align-center">
                            <v-icon class="ml-2" color="primary"
                                >mdi-account-supervisor</v-icon
                            >
                            <h3 class="primary--text">إدارة المشرفين</h3>
                        </div>
                    </v-col>
                    <v-spacer />
                    <v-col cols="12" md="4">
                        <div class="d-flex justify-space-between">
                            <v-text-field
                                v-model="filters.search"
                                v-debounce:700ms="handleSearch"
                                @click:clear="clearSearch"
                                placeholder="ابحث عن المشرفين (الاسم، اسم المستخدم، الايميل)"
                                hide-details
                                dense
                                class="pl-3"
                                outlined
                                prepend-inner-icon="mdi-magnify"
                                clearable
                            />
                            <v-select
                                @change="handlePageSizeChange"
                                :height="42"
                                v-model="pagination.pageSize"
                                :items="pageSizes"
                                no-data-text="لا توجد بيانات"
                                style="max-width: 85px"
                                hide-details
                                outlined
                                dense
                            ></v-select>
                        </div>
                    </v-col>
                </v-row>
            </div>

            <!-- Statistics Cards -->
            <v-row class="px-3 pb-3">
                <v-col cols="6" md="3">
                    <v-card class="text-center pa-3" color="primary" dark>
                        <v-icon size="40" class="mb-2"
                            >mdi-account-group</v-icon
                        >
                        <h3>{{ totalAdmins }}</h3>
                        <p class="mb-0">إجمالي المشرفين</p>
                    </v-card>
                </v-col>
                <v-col cols="6" md="3">
                    <v-card class="text-center pa-3" color="success" dark>
                        <v-icon size="40" class="mb-2"
                            >mdi-account-check</v-icon
                        >
                        <h3>{{ activeAdmins }}</h3>
                        <p class="mb-0">المشرفين النشطين</p>
                    </v-card>
                </v-col>
                <v-col cols="6" md="3">
                    <v-card class="text-center pa-3" color="warning" dark>
                        <v-icon size="40" class="mb-2">mdi-crown</v-icon>
                        <h3>{{ superAdmins }}</h3>
                        <p class="mb-0">المشرفين الرئيسيين</p>
                    </v-card>
                </v-col>
                <v-col cols="6" md="3">
                    <v-card class="text-center pa-3" color="info" dark>
                        <v-icon size="40" class="mb-2"
                            >mdi-shield-account</v-icon
                        >
                        <h3>{{ administrators }}</h3>
                        <p class="mb-0">المدراء</p>
                    </v-card>
                </v-col>
            </v-row>

            <!-- Data Table -->
            <v-card flat tile>
                <v-data-table
                    class="cursor-pointer"
                    :items="admins"
                    :loading="loading"
                    :headers="headers"
                    hide-default-footer
                    loading-text="جاري تحميل البيانات..."
                    no-data-text="لا توجد بيانات"
                    :id="
                        $vuetify.theme.dark
                            ? 'row_colors_dark'
                            : 'row_colors_light'
                    "
                    :items-per-page="pagination.pageSize"
                >
                    <template v-slot:[`item.createdAt`]="{ item }">
                        <UserInfo :item="item" />
                        <!-- {{ item.updatedUser }} -->
                    </template>
                    <!-- Avatar and Full Name -->
                    <template v-slot:[`item.fullName`]="{ item }">
                        <div class="d-flex align-center py-2">
                            <v-avatar size="40" class="ml-3">
                                <img
                                    v-if="item.pictureProfileUrl"
                                    :src="`${baseUrl}/${item.pictureProfileUrl}`"
                                    :alt="item.fullName"
                                />
                                <v-icon v-else color="primary"
                                    >mdi-account</v-icon
                                >
                            </v-avatar>
                            <div>
                                <div class="font-weight-bold">
                                    {{ item.fullName }}
                                </div>
                                <div class="text--secondary caption">
                                    {{ item.userName }}
                                </div>
                            </div>
                        </div>
                    </template>

                    <!-- Contact Information -->
                    <template v-slot:[`item.contact`]="{ item }">
                        <div>
                            <div
                                class="d-flex align-center mb-1"
                                v-if="item.email"
                            >
                                <v-icon small class="ml-1" color="primary"
                                    >mdi-email</v-icon
                                >
                                <span class="caption">{{ item.email }}</span>
                            </div>
                            <div
                                class="d-flex align-center"
                                v-if="item.phoneNumber"
                            >
                                <v-icon small class="ml-1" color="success"
                                    >mdi-phone</v-icon
                                >
                                <span class="caption" style="direction: rtl">{{
                                    item.phoneNumber
                                }}</span>
                            </div>
                        </div>
                    </template>

                    <!-- Status and Activity -->
                    <!-- <template v-slot:[`item.isActive`]="{ item }">
                        <v-chip
                            :color="item.isActive ? 'success' : 'error'"
                            :text-color="item.isActive ? 'white' : 'white'"
                            small
                        >
                            <v-icon left small>
                                {{
                                    item.isActive
                                        ? "mdi-check-circle"
                                        : "mdi-close-circle"
                                }}
                            </v-icon>
                            {{ item.isActive ? "نشط" : "غير نشط" }}
                        </v-chip>
                    </template> -->

                    <!-- Roles -->
                    <template v-slot:[`item.roles`]="{ item }">
                        <div class="d-flex flex-wrap">
                            <!-- Show first 2 roles -->
                            <v-chip
                                v-for="role in item.roles.slice(0, 2)"
                                :key="role"
                                :color="getRoleColor(role)"
                                small
                                class="ma-1"
                                text-color="white"
                            >
                                <v-icon left x-small>{{
                                    getRoleIcon(role)
                                }}</v-icon>
                                {{ getRoleDisplayName(role) }}
                            </v-chip>

                            <!-- Show +N more if there are more than 2 roles -->
                            <v-menu
                                bottom
                                left
                                offset-y
                                v-if="item.roles && item.roles.length > 2"
                            >
                                <template v-slot:activator="{ on, attrs }">
                                    <v-chip
                                        v-bind="attrs"
                                        v-on="on"
                                        small
                                        class="ma-1"
                                        color="grey"
                                        text-color="white"
                                        style="cursor: pointer"
                                    >
                                        +{{ item.roles.length - 2 }} أخرى
                                    </v-chip>
                                </template>
                                <v-card max-width="300">
                                    <v-card-title class="py-2 primary--text">
                                        <v-icon color="primary" class="ml-2"
                                            >mdi-shield-account</v-icon
                                        >
                                        جميع الأدوار
                                    </v-card-title>
                                    <v-divider></v-divider>
                                    <v-card-text
                                        class="pa-2"
                                        style="
                                            max-height: 250px;
                                            overflow-y: auto;
                                        "
                                    >
                                        <v-chip
                                            v-for="role in item.roles"
                                            :key="'menu-' + role"
                                            :color="getRoleColor(role)"
                                            small
                                            class="ma-1"
                                            text-color="white"
                                        >
                                            <v-icon left x-small>{{
                                                getRoleIcon(role)
                                            }}</v-icon>
                                            {{ getRoleDisplayName(role) }}
                                        </v-chip>
                                    </v-card-text>
                                </v-card>
                            </v-menu>
                        </div>
                    </template>

                    <!-- Birth Date -->
                    <template v-slot:[`item.dateBirth`]="{ item }">
                        <span v-if="item.dateBirth">{{
                            formatDate(item.dateBirth)
                        }}</span>
                        <span v-else class="text--secondary">غير محدد</span>
                    </template>

                    <!-- Created Date -->
                    <!-- <template v-slot:[`item.createdAt`]="{ item }">
                        <div>
                            <div>{{ formatDate(item.createdAt) }}</div>
                            <div class="caption text--secondary">
                                {{ formatTime(item.createdAt) }}
                            </div>
                        </div>
                    </template> -->

                    <!-- Address -->
                    <template v-slot:[`item.fullAddress`]="{ item }">
                        <div
                            v-if="item.fullAddress"
                            class="d-flex align-center"
                        >
                            <v-icon small class="ml-1" color="info"
                                >mdi-map-marker</v-icon
                            >
                            <span>{{ item.fullAddress }}</span>
                        </div>
                        <span v-else class="text--secondary">غير محدد</span>
                    </template>

                    <!-- Actions -->
                    <template v-slot:[`item.actions`]="{ item }">
                        <v-menu bottom left>
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn icon v-bind="attrs" v-on="on">
                                    <v-icon>mdi-dots-vertical</v-icon>
                                </v-btn>
                            </template>
                            <v-list dense>
                                <v-list-item @click="viewAdmin(item)">
                                    <v-list-item-icon>
                                        <v-icon>mdi-eye</v-icon>
                                    </v-list-item-icon>
                                    <v-list-item-content
                                        >عرض التفاصيل</v-list-item-content
                                    >
                                </v-list-item>
                                <v-list-item
                                    @click="editAdmin(item)"
                                    v-if="
                                        hasRole([
                                            Roles.AdminUpdate,
                                            Roles.Admin,
                                            Roles.Administrator,
                                            Roles.SuperAdmin,
                                        ])
                                    "
                                >
                                    <v-list-item-icon>
                                        <v-icon>mdi-pencil</v-icon>
                                    </v-list-item-icon>
                                    <v-list-item-content
                                        >تعديل</v-list-item-content
                                    >
                                </v-list-item>
                                <v-list-item
                                    @click="changeAdminPassword(item)"
                                    v-if="
                                        hasRole([
                                            Roles.AdminChangePassword,
                                            Roles.Admin,
                                            Roles.Administrator,
                                            Roles.SuperAdmin,
                                        ])
                                    "
                                >
                                    <v-list-item-icon>
                                        <v-icon>mdi-lock-reset</v-icon>
                                    </v-list-item-icon>
                                    <v-list-item-content
                                        >تغيير كلمة المرور</v-list-item-content
                                    >
                                </v-list-item>
                                <v-divider
                                    v-if="
                                        hasRole([
                                            Roles.AdminUpdate,
                                            Roles.AdminChangePassword,
                                            Roles.Admin,
                                            Roles.Administrator,
                                            Roles.SuperAdmin,
                                        ])
                                    "
                                ></v-divider>
                                <v-list-item
                                    v-if="
                                        hasRole([
                                            Roles.AdminDelete,
                                            Roles.Admin,
                                            Roles.Administrator,
                                            Roles.SuperAdmin,
                                        ])
                                    "
                                    @click="deleteAdmin(item)"
                                    class="error--text"
                                >
                                    <v-list-item-icon>
                                        <v-icon color="error">
                                            mdi-delete
                                        </v-icon>
                                    </v-list-item-icon>
                                    <v-list-item-content>
                                        حذف المشرف
                                    </v-list-item-content>
                                </v-list-item>
                            </v-list>
                        </v-menu>
                    </template>
                </v-data-table>

                <!-- Custom Pagination -->
                <div class="d-flex justify-center align-center pa-4">
                    <v-pagination
                        v-model="pagination.pageIndex"
                        :length="totalPages"
                        :total-visible="7"
                        @input="handlePageChange"
                        circle
                    ></v-pagination>
                </div>

                <div
                    class="d-flex justify-space-between align-center px-4 pb-4"
                >
                    <div class="text--secondary">
                        عرض {{ startItem }} - {{ endItem }} من أصل
                        {{ totalItems }} عنصر
                    </div>
                    <div class="text--secondary">
                        الصفحة {{ pagination.pageIndex }} من {{ totalPages }}
                    </div>
                </div>
            </v-card>
        </v-card>

        <!-- Admin Details Dialog -->
        <v-dialog v-model="adminDetailsDialog" max-width="800" persistent>
            <v-card v-if="selectedAdmin">
                <v-card-title class="primary white--text">
                    <v-icon left color="white">mdi-account-details</v-icon>
                    تفاصيل المشرف
                    <v-spacer></v-spacer>
                    <v-btn icon dark @click="adminDetailsDialog = false">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>

                <v-card-text class="pa-4">
                    <v-row>
                        <v-col cols="12" md="4" class="text-center">
                            <v-avatar size="120" class="mb-3">
                                <img
                                    v-if="selectedAdmin.pictureProfileUrl"
                                    :src="`${baseUrl}/${selectedAdmin.pictureProfileUrl}`"
                                    :alt="selectedAdmin.fullName"
                                />
                                <v-icon v-else size="80" color="primary"
                                    >mdi-account</v-icon
                                >
                            </v-avatar>
                            <h3>{{ selectedAdmin.fullName }}</h3>
                            <p class="text--secondary">
                                {{ selectedAdmin.userName }}
                            </p>
                            <v-chip
                                :color="
                                    selectedAdmin.isActive ? 'success' : 'error'
                                "
                                text-color="white"
                                small
                            >
                                {{ selectedAdmin.isActive ? "نشط" : "غير نشط" }}
                            </v-chip>
                        </v-col>
                        <v-col cols="12" md="8">
                            <v-simple-table>
                                <tbody>
                                    <tr>
                                        <td class="font-weight-bold">
                                            الاسم الكامل:
                                        </td>
                                        <td>{{ selectedAdmin.fullName }}</td>
                                    </tr>
                                    <tr>
                                        <td class="font-weight-bold">
                                            اسم المستخدم:
                                        </td>
                                        <td>{{ selectedAdmin.userName }}</td>
                                    </tr>
                                    <tr>
                                        <td class="font-weight-bold">
                                            البريد الإلكتروني:
                                        </td>
                                        <td>
                                            {{
                                                selectedAdmin.email ||
                                                "غير محدد"
                                            }}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="font-weight-bold">
                                            رقم الهاتف:
                                        </td>
                                        <td>
                                            {{
                                                selectedAdmin.phoneNumber ||
                                                "غير محدد"
                                            }}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="font-weight-bold">
                                            العنوان:
                                        </td>
                                        <td>
                                            {{
                                                selectedAdmin.fullAddress ||
                                                "غير محدد"
                                            }}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="font-weight-bold">
                                            تاريخ الميلاد:
                                        </td>
                                        <td>
                                            {{
                                                selectedAdmin.dateBirth
                                                    ? formatDate(
                                                          selectedAdmin.dateBirth
                                                      )
                                                    : "غير محدد"
                                            }}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="font-weight-bold">
                                            تاريخ الإنشاء:
                                        </td>
                                        <td>
                                            {{
                                                formatDate(
                                                    selectedAdmin.createdAt
                                                )
                                            }}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="font-weight-bold">
                                            المحافظة:
                                        </td>
                                        <td>
                                            {{
                                                selectedAdmin.provinceId ||
                                                "غير محدد"
                                            }}
                                        </td>
                                    </tr>
                                    <tr v-if="selectedAdmin.notes">
                                        <td class="font-weight-bold">
                                            ملاحظات:
                                        </td>
                                        <td>{{ selectedAdmin.notes }}</td>
                                    </tr>
                                </tbody>
                            </v-simple-table>

                            <div class="mt-4">
                                <h4 class="mb-2">الأدوار والصلاحيات:</h4>
                                <v-chip
                                    v-for="role in selectedAdmin.roles"
                                    :key="role"
                                    :color="getRoleColor(role)"
                                    class="ma-1"
                                    text-color="white"
                                >
                                    <v-icon left small>{{
                                        getRoleIcon(role)
                                    }}</v-icon>
                                    {{ getRoleDisplayName(role) }}
                                </v-chip>
                            </div>

                            <div class="mt-3">
                                <v-row>
                                    <v-col cols="4">
                                        <v-chip
                                            :color="
                                                selectedAdmin.isSuperAdmin
                                                    ? 'warning'
                                                    : 'grey'
                                            "
                                            small
                                        >
                                            <v-icon left x-small
                                                >mdi-crown</v-icon
                                            >
                                            {{
                                                selectedAdmin.isSuperAdmin
                                                    ? "مشرف رئيسي"
                                                    : "ليس مشرف رئيسي"
                                            }}
                                        </v-chip>
                                    </v-col>
                                    <v-col cols="4">
                                        <v-chip
                                            :color="
                                                selectedAdmin.isAdministrator
                                                    ? 'info'
                                                    : 'grey'
                                            "
                                            small
                                        >
                                            <v-icon left x-small
                                                >mdi-shield-account</v-icon
                                            >
                                            {{
                                                selectedAdmin.isAdministrator
                                                    ? "مدير"
                                                    : "ليس مدير"
                                            }}
                                        </v-chip>
                                    </v-col>
                                </v-row>
                            </div>
                        </v-col>
                    </v-row>
                </v-card-text>

                <v-card-actions class="pa-4">
                    <v-btn color="primary" @click="editAdmin(selectedAdmin)">
                        <v-icon left>mdi-pencil</v-icon>
                        تعديل
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn text @click="adminDetailsDialog = false"
                        >إغلاق</v-btn
                    >
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Add/Edit Admin Dialog -->
        <v-dialog
            v-model="adminFormDialog"
            max-width="1000"
            persistent
            :fullscreen="isFullscreen"
            transition="dialog-bottom-transition"
        >
            <v-toolbar height="45" flat color="primary white--text">
                <h5>
                    {{
                        dialogType === "add"
                            ? "إضافة مشرف جديد"
                            : "تعديل بيانات المشرف"
                    }}
                </h5>
                <v-spacer />
                <v-btn small icon fab @click="isFullscreen = !isFullscreen">
                    <v-icon small color="white">
                        {{
                            isFullscreen
                                ? "mdi-arrow-collapse"
                                : "mdi-arrow-expand"
                        }}
                    </v-icon>
                </v-btn>
                <v-btn @click="closeAdminDialog()" fab small icon color="white">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-toolbar>
            <v-card flat class="px-1">
                <!-- Dialog Toolbar -->
                <v-divider class="mx-4" />

                <!-- Form Body -->
                <v-form
                    @submit.prevent="submitAdmin()"
                    ref="adminForm"
                    lazy-validation
                    v-model="formValid"
                >
                    <v-container class="overflow-y-auto" style="height: 70vh">
                        <v-card flat class="py-4 px-1">
                            <!-- Profile Picture Section -->
                            <v-row class="mb-4">
                                <v-col cols="12" class="text-center">
                                    <div class="mb-3">
                                        <v-avatar size="120" class="mb-3">
                                            <img
                                                v-if="
                                                    adminForm.pictureProfile ||
                                                    adminForm.pictureProfileUrl
                                                "
                                                :src="`${
                                                    adminForm.pictureProfile ||
                                                    baseUrl +
                                                        '/' +
                                                        adminForm.pictureProfileUrl
                                                }`"
                                                :alt="adminForm.fullName"
                                            />
                                            <v-icon
                                                v-else
                                                size="80"
                                                color="primary"
                                                >mdi-account</v-icon
                                            >
                                        </v-avatar>
                                    </div>
                                    <v-file-input
                                        v-model="profileImageFile"
                                        @change="handleImageUpload"
                                        accept="image/jpeg, image/png , image/jpg "
                                        prepend-icon="mdi-camera"
                                        label="صورة المشرف"
                                        show-size
                                        truncate-length="15"
                                        outlined
                                        dense
                                    ></v-file-input>
                                </v-col>
                            </v-row>

                            <v-row>
                                <!-- Full Name -->
                                <v-col cols="12" md="6">
                                    <h6 class="mb-1">
                                        الاسم الكامل
                                        <span class="required">*</span>
                                    </h6>
                                    <v-text-field
                                        v-model="adminForm.fullName"
                                        :rules="[rules.required]"
                                        outlined
                                        dense
                                        placeholder="الاسم الكامل"
                                    ></v-text-field>
                                </v-col>

                                <!-- Username -->
                                <v-col cols="12" md="6">
                                    <h6 class="mb-1">
                                        اسم المستخدم
                                        <span class="required">*</span>
                                    </h6>
                                    <v-text-field
                                        v-model="adminForm.userName"
                                        :rules="[
                                            rules.required,
                                            rules.englishOnly,
                                        ]"
                                        outlined
                                        dense
                                        placeholder="اسم المستخدم (بالإنجليزية)"
                                        hint="يجب أن يكون باللغة الإنجليزية فقط"
                                    ></v-text-field>
                                </v-col>

                                <!-- Phone Number -->
                                <v-col cols="12" md="6">
                                    <h6 class="mb-1">
                                        رقم الهاتف
                                        <span class="required">*</span>
                                    </h6>
                                    <v-text-field
                                        v-model="adminForm.phoneNumber"
                                        :rules="[
                                            rules.required,
                                            rules.phoneFormat,
                                        ]"
                                        outlined
                                        dense
                                        placeholder="07821027100"
                                        dir="ltr"
                                    ></v-text-field>
                                </v-col>

                                <!-- Email -->
                                <v-col cols="12" md="6">
                                    <h6 class="mb-1">البريد الإلكتروني</h6>
                                    <v-text-field
                                        v-model="adminForm.email"
                                        :rules="[rules.emailFormat]"
                                        outlined
                                        dense
                                        placeholder="example@domain.com"
                                        dir="ltr"
                                    ></v-text-field>
                                </v-col>

                                <!-- Password -->
                                <v-col
                                    cols="12"
                                    md="6"
                                    v-if="dialogType === 'add'"
                                >
                                    <h6 class="mb-1">
                                        كلمة المرور
                                        <span class="required">*</span>
                                    </h6>
                                    <v-text-field
                                        v-model="adminForm.password"
                                        :rules="[
                                            rules.required,
                                            rules.minLength(6),
                                        ]"
                                        :type="
                                            showPassword ? 'text' : 'password'
                                        "
                                        :append-icon="
                                            showPassword
                                                ? 'mdi-eye'
                                                : 'mdi-eye-off'
                                        "
                                        @click:append="
                                            showPassword = !showPassword
                                        "
                                        outlined
                                        dense
                                        placeholder="كلمة المرور"
                                        hint="يجب أن تكون 6 أحرف على الأقل"
                                    ></v-text-field>
                                </v-col>

                                <!-- Confirm Password -->
                                <v-col
                                    cols="12"
                                    md="6"
                                    v-if="dialogType === 'add'"
                                >
                                    <h6 class="mb-1">
                                        تأكيد كلمة المرور
                                        <span class="required">*</span>
                                    </h6>
                                    <v-text-field
                                        v-model="confirmPassword"
                                        :rules="[
                                            rules.required,
                                            rules.passwordMatch,
                                        ]"
                                        :type="
                                            showConfirmPassword
                                                ? 'text'
                                                : 'password'
                                        "
                                        :append-icon="
                                            showConfirmPassword
                                                ? 'mdi-eye'
                                                : 'mdi-eye-off'
                                        "
                                        @click:append="
                                            showConfirmPassword =
                                                !showConfirmPassword
                                        "
                                        outlined
                                        dense
                                        placeholder="تأكيد كلمة المرور"
                                    ></v-text-field>
                                </v-col>

                                <!-- Province -->
                                <v-col cols="12" md="6">
                                    <h6 class="mb-1">المحافظة</h6>
                                    <v-autocomplete
                                        v-model="adminForm.provinceId"
                                        :items="$global.state.provinces"
                                        item-text="name"
                                        item-value="id"
                                        outlined
                                        dense
                                        placeholder="اختر المحافظة"
                                        no-data-text="لا توجد بيانات"
                                        prepend-inner-icon="mdi-map-marker"
                                        clearable
                                    ></v-autocomplete>
                                </v-col>

                                <!-- Birth Date -->
                                <v-col cols="12" md="6">
                                    <h6 class="mb-1">تاريخ الميلاد</h6>
                                    <v-menu
                                        v-model="birthDateMenu"
                                        :close-on-content-click="false"
                                        :nudge-right="40"
                                        transition="scale-transition"
                                        offset-y
                                        min-width="auto"
                                    >
                                        <template
                                            v-slot:activator="{ on, attrs }"
                                        >
                                            <v-text-field
                                                v-model="adminForm.dateBirth"
                                                prepend-inner-icon="mdi-calendar"
                                                readonly
                                                v-bind="attrs"
                                                v-on="on"
                                                outlined
                                                dense
                                                placeholder="اختر تاريخ الميلاد"
                                                clearable
                                            ></v-text-field>
                                        </template>
                                        <v-date-picker
                                            v-model="adminForm.dateBirth"
                                            @input="birthDateMenu = false"
                                        ></v-date-picker>
                                    </v-menu>
                                </v-col>

                                <!-- Full Address -->
                                <v-col cols="12">
                                    <h6 class="mb-1">العنوان الكامل</h6>
                                    <v-textarea
                                        v-model="adminForm.fullAddress"
                                        outlined
                                        dense
                                        placeholder="العنوان الكامل"
                                        rows="3"
                                        auto-grow
                                    ></v-textarea>
                                </v-col>

                                <!-- Notes -->
                                <v-col cols="12">
                                    <h6 class="mb-1">ملاحظات</h6>
                                    <v-textarea
                                        v-model="adminForm.notes"
                                        outlined
                                        dense
                                        placeholder="ملاحظات إضافية"
                                        rows="3"
                                        auto-grow
                                    ></v-textarea>
                                </v-col>

                                <!-- Roles -->
                                <v-col cols="12">
                                    <h6 class="mb-1">
                                        الأدوار والصلاحيات
                                        <span class="required">*</span>
                                    </h6>
                                    <v-autocomplete
                                        v-model="adminForm.roles"
                                        :items="availableRoles"
                                        item-text="displayName"
                                        item-value="value"
                                        :rules="[rules.required]"
                                        outlined
                                        dense
                                        placeholder="اختر الأدوار"
                                        no-data-text="لا توجد بيانات"
                                        prepend-inner-icon="mdi-shield-account"
                                        multiple
                                        chips
                                        deletable-chips
                                        clearable
                                    >
                                        <template v-slot:selection="{ item }">
                                            <v-chip
                                                :color="
                                                    getRoleColor(item.value)
                                                "
                                                text-color="white"
                                                small
                                                close
                                                @click:close="
                                                    removeRole(item.value)
                                                "
                                            >
                                                <v-icon left x-small>{{
                                                    getRoleIcon(item.value)
                                                }}</v-icon>
                                                {{ item.displayName }}
                                            </v-chip>
                                        </template>
                                    </v-autocomplete>
                                </v-col>

                                <!-- Special Permissions -->
                                <!-- <v-col cols="12">
                                    <h6 class="mb-3">صلاحيات خاصة</h6>
                                    <v-row>
                                        <v-col cols="12" md="6">
                                            <v-switch
                                                v-model="adminForm.isSuperAdmin"
                                                label="مشرف رئيسي"
                                                color="warning"
                                                hide-details
                                            >
                                                <template v-slot:label>
                                                    <div
                                                        class="d-flex align-center"
                                                    >
                                                        <v-icon
                                                            color="warning"
                                                            class="ml-2"
                                                            >mdi-crown</v-icon
                                                        >
                                                        <span>مشرف رئيسي</span>
                                                    </div>
                                                </template>
                                            </v-switch>
                                        </v-col>
                                        <v-col cols="12" md="6">
                                            <v-switch
                                                v-model="
                                                    adminForm.isAdministrator
                                                "
                                                label="مدير"
                                                color="info"
                                                hide-details
                                            >
                                                <template v-slot:label>
                                                    <div
                                                        class="d-flex align-center"
                                                    >
                                                        <v-icon
                                                            color="info"
                                                            class="ml-2"
                                                            >mdi-shield-account</v-icon
                                                        >
                                                        <span>مدير</span>
                                                    </div>
                                                </template>
                                            </v-switch>
                                        </v-col>
                                    </v-row>
                                </v-col> -->
                            </v-row>
                        </v-card>
                    </v-container>

                    <!-- Dialog Actions -->
                    <v-card-actions class="pa-4">
                        <!-- @click="submitAdmin" -->
                        <v-btn
                            color="primary"
                            :loading="formSubmitting"
                            :disabled="!formValid"
                            type="submit"
                        >
                            <v-icon left>{{
                                dialogType === "add"
                                    ? "mdi-plus"
                                    : "mdi-content-save"
                            }}</v-icon>
                            {{
                                dialogType === "add"
                                    ? "إضافة المشرف"
                                    : "حفظ التعديلات"
                            }}
                        </v-btn>
                        <v-spacer></v-spacer>
                        <v-btn text @click="closeAdminDialog()">إلغاء</v-btn>
                    </v-card-actions>
                </v-form>
            </v-card>
        </v-dialog>

        <!-- Change Admin Password Dialog -->
        <v-dialog v-model="changePasswordDialog" max-width="500" persistent>
            <v-card>
                <v-card-title class="headline d-flex align-center">
                    <v-icon class="ml-2" color="primary">mdi-lock-reset</v-icon>
                    تغيير كلمة مرور المشرف
                </v-card-title>
                <v-divider></v-divider>
                <v-form
                    ref="changePasswordForm"
                    v-model="changePasswordValid"
                    lazy-validation
                >
                    <v-card-text>
                        <v-row>
                            <v-col cols="12">
                                <div class="mb-3 pa-3 grey lighten-4 rounded">
                                    <v-icon class="ml-2" small
                                        >mdi-account</v-icon
                                    >
                                    <strong>{{
                                        selectedAdmin?.fullName
                                    }}</strong>
                                    <br />
                                    <small class="text--secondary">{{
                                        selectedAdmin?.email
                                    }}</small>
                                </div>
                            </v-col>
                            <v-col cols="12">
                                <v-text-field
                                    v-model="changePasswordForm.newPassword"
                                    :rules="[
                                        $global.state.required(
                                            'كلمة المرور الجديدة'
                                        ),
                                        $validations.passwordFormat(),
                                    ]"
                                    :type="
                                        showChangeNewPassword
                                            ? 'text'
                                            : 'password'
                                    "
                                    :append-icon="
                                        showChangeNewPassword
                                            ? 'mdi-eye'
                                            : 'mdi-eye-off'
                                    "
                                    @click:append="
                                        showChangeNewPassword =
                                            !showChangeNewPassword
                                    "
                                    label="كلمة المرور الجديدة *"
                                    placeholder="أدخل كلمة المرور الجديدة"
                                    outlined
                                    dense
                                    hide-details="auto"
                                    required
                                ></v-text-field>
                            </v-col>
                            <v-col cols="12">
                                <v-text-field
                                    v-model="changePasswordForm.confirmPassword"
                                    :rules="[
                                        $global.state.required(
                                            'تأكيد كلمة المرور'
                                        ),
                                        validatePasswordMatch,
                                    ]"
                                    :type="
                                        showChangeConfirmPassword
                                            ? 'text'
                                            : 'password'
                                    "
                                    :append-icon="
                                        showChangeConfirmPassword
                                            ? 'mdi-eye'
                                            : 'mdi-eye-off'
                                    "
                                    @click:append="
                                        showChangeConfirmPassword =
                                            !showChangeConfirmPassword
                                    "
                                    label="تأكيد كلمة المرور الجديدة *"
                                    placeholder="أدخل كلمة المرور مرة أخرى"
                                    outlined
                                    dense
                                    hide-details="auto"
                                    required
                                ></v-text-field>
                            </v-col>
                            <v-col cols="12">
                                <v-alert type="info" text dense class="mb-0">
                                    <small>
                                        <!-- <v-icon small class="ml-1"
                                            >mdi-information</v-icon
                                        > -->
                                        كلمة المرور يجب أن تحتوي على 6 أحرف على
                                        الأقل، حرف كبير، حرف صغير، رقم، ورمز خاص
                                    </small>
                                </v-alert>
                            </v-col>
                        </v-row>
                    </v-card-text>
                    <v-divider></v-divider>
                    <v-card-actions>
                        <v-btn
                            color="primary"
                            :loading="changePasswordLoading"
                            :disabled="!changePasswordValid"
                            @click="submitChangePassword"
                        >
                            <v-icon small class="ml-1">mdi-content-save</v-icon>
                            تغيير كلمة المرور
                        </v-btn>
                        <v-spacer></v-spacer>
                        <v-btn
                            text
                            :disabled="changePasswordLoading"
                            @click="closeChangePasswordDialog"
                        >
                            إلغاء
                        </v-btn>
                    </v-card-actions>
                </v-form>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import axios from "@/axios";
import genericService from "@/services/genericService";
import { CustomRoles, Roles } from "../../utils/roles";
import config from "@/../public/static/config.json";
import UserInfo from "../../components/UserInfo.vue";
import validations from "@/utils/validations";

export default {
    name: "AdminUsers",
    data() {
        return {
            Roles,
            // UI State
            loading: false,
            showFilters: false,
            adminDetailsDialog: false,
            selectedAdmin: null,

            // Add/Edit Dialog State
            adminFormDialog: false,
            dialogType: "add", // 'add' or 'edit'
            isFullscreen: false,
            formValid: false,

            // Change Password Dialog State
            changePasswordDialog: false,
            changePasswordValid: false,
            changePasswordLoading: false,
            showChangeNewPassword: false,
            showChangeConfirmPassword: false,
            changePasswordForm: {
                newPassword: "",
                confirmPassword: "",
            },
            $validations: validations,
            formSubmitting: false,
            showPassword: false,
            showConfirmPassword: false,
            birthDateMenu: false,
            profileImageFile: null,
            confirmPassword: "",

            // Data
            admins: [],
            totalItems: 0,
            provinces: [
                { id: 1, name: "بغداد" },
                { id: 2, name: "البصرة" },
                { id: 3, name: "نينوى" },
                { id: 4, name: "أربيل" },
                { id: 5, name: "النجف" },
                { id: 6, name: "كربلاء" },
                { id: 7, name: "بابل" },
                { id: 8, name: "الديوانية" },
                { id: 9, name: "ذي قار" },
                { id: 10, name: "المثنى" },
                { id: 11, name: "كركوك" },
                { id: 12, name: "الأنبار" },
                { id: 13, name: "صلاح الدين" },
                { id: 14, name: "ديالى" },
                { id: 15, name: "ميسان" },
                { id: 16, name: "واسط" },
                { id: 17, name: "دهوك" },
                { id: 18, name: "السليمانية" },
            ],
            availableRoles: CustomRoles.filter(
                (role) => role.Name !== Roles.Administrator
            ).map((role) => ({
                value: role.Name,
                displayName: role.ArCaption,
            })),

            // Admin Form Data
            adminForm: {
                fullName: "",
                userName: "",
                phoneNumber: "",
                email: "",
                password: "",
                pictureProfile: "",
                pictureProfileUrl: "",
                provinceId: null,
                fullAddress: "",
                notes: "",
                dateBirth: "",
                roles: [],
                isSuperAdmin: false,
                isAdministrator: false,
            },

            // Form Validation Rules
            rules: {
                required: (value) => !!value || "هذا الحقل مطلوب",
                emailFormat: (value) => {
                    if (!value) return true;
                    const pattern =
                        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
                    return pattern.test(value) || "البريد الإلكتروني غير صحيح";
                },
                englishOnly: (value) => {
                    if (!value) return true;
                    const pattern = /^[a-zA-Z0-9_]+$/;
                    return (
                        pattern.test(value) ||
                        "يجب أن يحتوي على أحرف إنجليزية وأرقام فقط"
                    );
                },
                phoneFormat: (value) => {
                    if (!value) return true;
                    // Accept both formats: "07821027100" and "+96407821027100"
                    const iraqiPhonePattern = /^(07[0-9]{9}|\+9647[0-9]{9})$/;
                    return (
                        iraqiPhonePattern.test(value.replace(/\s+/g, "")) ||
                        "تنسيق رقم الهاتف غير صحيح (مثال: 07821027100)"
                    );
                },
                minLength: (min) => (value) => {
                    if (!value) return true;
                    return (
                        value.length >= min ||
                        `يجب أن يكون ${min} أحرف على الأقل`
                    );
                },
                passwordMatch: (value) => {
                    return (
                        value === this.adminForm.password ||
                        "كلمات المرور غير متطابقة"
                    );
                },
            },

            // Pagination
            pagination: {
                pageIndex: 1,
                pageSize: 20,
            },
            pageSizes: [10, 20, 50, 100],

            // Filters
            filters: {
                search: "",
                sort: "",
            },
            sortDirection: "desc",

            // Sort Options
            sortOptions: [
                { text: "اسم المستخدم", value: "UserName" },
                { text: "الاسم الكامل", value: "FullName" },
                { text: "تاريخ الإنشاء", value: "CreatedAt" },
                { text: "تاريخ الميلاد", value: "DateBirth" },
                { text: "المحافظة", value: "ProvinceId" },
            ],

            // Table Headers
            headers: [
                {
                    text: "المشرف",
                    value: "fullName",
                    sortable: false,
                    width: "250px",
                },
                {
                    text: "معلومات الاتصال",
                    value: "contact",
                    sortable: false,
                    width: "200px",
                },
                // {
                //     text: "الحالة",
                //     value: "isActive",
                //     sortable: false,
                //     width: "100px",
                // },
                {
                    text: "الأدوار",
                    value: "roles",
                    sortable: false,
                    width: "200px",
                },
                {
                    text: "تاريخ الميلاد",
                    value: "dateBirth",
                    sortable: false,
                    width: "120px",
                },
                // {
                //     text: "تاريخ الإنشاء",
                //     value: "createdAt",
                //     sortable: false,
                //     width: "150px",
                // },
                {
                    text: "العنوان",
                    value: "fullAddress",
                    sortable: false,
                    width: "150px",
                },
                {
                    text: "آخر تحديث بواسطة",
                    value: "createdAt",
                    width: "150px",
                },
                {
                    text: "الإجراءات",
                    value: "actions",
                    sortable: false,
                    width: "100px",
                },
            ],
        };
    },

    computed: {
        baseUrl() {
            return process.env.NODE_ENV === "production"
                ? config.APP_URL_PROD
                : config.APP_URL_DEV;
        },

        totalPages() {
            return Math.ceil(this.totalItems / this.pagination.pageSize);
        },

        startItem() {
            return (
                (this.pagination.pageIndex - 1) * this.pagination.pageSize + 1
            );
        },

        endItem() {
            const end = this.pagination.pageIndex * this.pagination.pageSize;
            return end > this.totalItems ? this.totalItems : end;
        },

        totalAdmins() {
            return this.admins.length;
        },

        activeAdmins() {
            return this.admins.filter((admin) => admin.isActive).length;
        },

        superAdmins() {
            return this.admins.filter((admin) => admin.isSuperAdmin).length;
        },

        administrators() {
            return this.admins.filter((admin) => admin.isAdministrator).length;
        },
    },

    mounted() {
        this.getAdmins();
    },
    components: {
        UserInfo: () => import("@/components/UserInfo.vue"),
    },
    methods: {
        // Dialog Methods
        openAddDialog() {
            this.dialogType = "add";
            this.resetAdminForm();
            this.adminFormDialog = true;
        },

        openEditDialog(admin) {
            this.dialogType = "edit";
            this.populateAdminForm(admin);
            this.adminFormDialog = true;
        },

        closeAdminDialog() {
            this.adminFormDialog = false;
            this.isFullscreen = false;
            this.resetAdminForm();
            this.$refs.adminForm?.resetValidation();
        },

        resetAdminForm() {
            this.adminForm = {
                fullName: "",
                userName: "",
                phoneNumber: "",
                email: "",
                password: "",
                pictureProfile: "",
                pictureProfileUrl: "",
                provinceId: null,
                fullAddress: "",
                notes: "",
                dateBirth: "",
                roles: [],
                isSuperAdmin: false,
                isAdministrator: false,
            };
            this.confirmPassword = "";
            this.profileImageFile = null;
            this.showPassword = false;
            this.showConfirmPassword = false;
        },

        // Change Password Methods
        changeAdminPassword(admin) {
            this.selectedAdmin = admin;
            this.resetChangePasswordForm();
            this.changePasswordDialog = true;
        },

        closeChangePasswordDialog() {
            this.changePasswordDialog = false;
            this.resetChangePasswordForm();
            this.$refs.changePasswordForm?.resetValidation();
        },

        resetChangePasswordForm() {
            this.changePasswordForm = {
                newPassword: "",
                confirmPassword: "",
            };
            this.showChangeNewPassword = false;
            this.showChangeConfirmPassword = false;
        },

        validatePasswordMatch() {
            if (
                this.changePasswordForm.newPassword !==
                this.changePasswordForm.confirmPassword
            ) {
                return "كلمات المرور غير متطابقة";
            }
            return true;
        },

        async submitChangePassword() {
            if (!this.$refs.changePasswordForm.validate()) {
                return;
            }

            this.changePasswordLoading = true;

            try {
                const response = await this.$http.put(
                    `Admin/change-password/${this.selectedAdmin.id}`,
                    {
                        newPassword: this.changePasswordForm.newPassword,
                        confirmPassword:
                            this.changePasswordForm.confirmPassword,
                    }
                );

                this.$store.dispatch("toastification/setToast", {
                    message:
                        response.data.message || "تم تغيير كلمة المرور بنجاح",
                    type: "success",
                });

                this.closeChangePasswordDialog();
            } catch (error) {
                console.error("Error changing admin password:", error);
                this.$store.dispatch("toastification/setToast", {
                    message:
                        error.response?.data?.message ||
                        "حدث خطأ أثناء تغيير كلمة المرور",
                    type: "error",
                });
            } finally {
                this.changePasswordLoading = false;
            }
        },

        populateAdminForm(admin) {
            this.adminForm = {
                id: admin.id,
                fullName: admin.fullName || "",
                userName: admin.userName || "",
                phoneNumber: admin.phoneNumber || "",
                email: admin.email || "",
                password: "", // Never populate password for security
                pictureProfile: "",
                pictureProfileUrl: admin.pictureProfileUrl || "",
                provinceId: admin.provinceId || null,
                fullAddress: admin.fullAddress || "",
                notes: admin.notes || "",
                dateBirth: admin.dateBirth ? admin.dateBirth.split("T")[0] : "",
                roles: admin.roles || [],
                isSuperAdmin: admin.isSuperAdmin || false,
                isAdministrator: admin.isAdministrator || false,
            };
        },

        handleImageUpload() {
            if (this.profileImageFile) {
                const reader = new FileReader();
                reader.onload = (e) => {
                    this.adminForm.pictureProfile = e.target.result;
                };
                reader.readAsDataURL(this.profileImageFile);
            }
        },

        removeRole(roleValue) {
            const index = this.adminForm.roles.indexOf(roleValue);
            if (index > -1) {
                this.adminForm.roles.splice(index, 1);
            }
        },

        async submitAdmin() {
            if (!this.$refs.adminForm.validate()) {
                return;
            }

            this.formSubmitting = true;
            try {
                const form = { ...this.adminForm };
                const fd = new FormData();

                // Required fields
                fd.append("FullName", form.fullName);
                fd.append("UserName", form.userName);
                fd.append("PhoneNumber", form.phoneNumber);

                // Optional fields - only append if they have values
                if (form.email) fd.append("Email", form.email);

                // Password - only for add mode
                if (this.dialogType === "add" && form.password) {
                    fd.append("Password", form.password);
                }

                // Picture Profile - use the actual file if uploaded, not the base64 preview
                if (this.profileImageFile) {
                    fd.append("PictureProfile", this.profileImageFile);
                }

                // Province ID - convert to string if exists
                if (form.provinceId) {
                    fd.append("ProvinceId", form.provinceId.toString());
                }

                // Optional text fields
                if (form.fullAddress)
                    fd.append("FullAddress", form.fullAddress);
                if (form.notes) fd.append("Notes", form.notes);

                // Date Birth
                if (form.dateBirth) {
                    fd.append("DateBirth", form.dateBirth);
                }

                // Roles - append each role separately
                if (form.roles && form.roles.length > 0) {
                    form.roles.forEach((role) => {
                        const roleValue =
                            typeof role === "object" ? role.value : role;
                        fd.append("Roles", roleValue);
                    });
                }

                let response;
                if (this.dialogType === "add") {
                    response = await axios
                        .post("/Admin", fd, {
                            headers: {
                                "Content-Type": "multipart/form-data",
                            },
                        })
                        .catch((error) => {
                            console.log(error);
                            if (error && error.data) {
                                if (error.data.result) {
                                    this.$toast.warning(error.data.result);
                                }
                            } else {
                                this.$toast.error("حدث خطأ أثناء حفظ البيانات");
                            }
                        });
                } else {
                    // For edit, include the ID
                    fd.append("Id", form.id);
                    response = await axios
                        .put(`/Admin/${form.id}`, fd, {
                            headers: {
                                "Content-Type": "multipart/form-data",
                            },
                        })
                        .catch((error) => {
                            if (error && error.data) {
                                if (error.data.result) {
                                    this.$toast.warning(error.data.result);
                                }
                            } else {
                                this.$toast.error("حدث خطأ أثناء حفظ البيانات");
                            }
                        });
                }

                if (
                    response.data.statusCode === 201 ||
                    response.data.statusCode === 200
                ) {
                    this.$toast.success(
                        this.dialogType === "add"
                            ? "تم إضافة المشرف بنجاح"
                            : "تم تحديث بيانات المشرف بنجاح"
                    );
                    this.closeAdminDialog();
                    this.getAdmins(); // Refresh the data
                } else {
                    console.log("err:", response);

                    // Check if there are validation errors in the response
                    if (
                        response.data.result &&
                        Array.isArray(response.data.result)
                    ) {
                        // Show validation errors as warnings
                        response.data.result.forEach((error) => {
                            this.$toast.warning(error);
                        });
                    } else if (response.data.result) {
                        this.$toast.warning(response.data.message);
                    } else {
                        this.$toast.error("حدث خطأ أثناء حفظ البيانات");
                    }
                }
            } catch (error) {
                console.error(error);
            } finally {
                this.formSubmitting = false;
            }
        },

        // Data Methods
        async getAdmins() {
            // Close all dialogs when refreshing data
            this.adminDetailsDialog = false;
            this.adminFormDialog = false;
            this.changePasswordDialog = false;

            this.loading = true;
            try {
                const params = {
                    PageIndex: this.pagination.pageIndex,
                    PageSize: this.pagination.pageSize,
                    Search: this.filters.search || "",
                    Sort: this.buildSortString(),
                };

                const response = await axios.get("/Admin", { params });

                if (response.data.statusCode === 200) {
                    this.admins = response.data.result.data;
                    this.totalItems = response.data.result.count;
                    this.pagination.pageIndex = response.data.result.pageIndex;
                    this.pagination.pageSize = response.data.result.pageSize;
                } else {
                    this.$toast.error("حدث خطأ أثناء تحميل البيانات");
                }
            } catch (error) {
                console.error("Error fetching admins:", error);
                this.$toast.error("حدث خطأ أثناء تحميل البيانات");
            } finally {
                this.loading = false;
            }
        },

        buildSortString() {
            if (this.filters.sort) {
                return `${this.filters.sort}:${this.sortDirection}`;
            }
            return "";
        },

        handleSearch() {
            this.pagination.pageIndex = 1;
            this.getAdmins();
        },

        clearSearch() {
            this.filters.search = "";
            this.pagination.pageIndex = 1;
            this.getAdmins();
        },

        handleFilterChange() {
            this.pagination.pageIndex = 1;
            this.getAdmins();
        },

        handlePageChange(page) {
            this.pagination.pageIndex = page;
            this.getAdmins();
        },

        handlePageSizeChange() {
            this.pagination.pageIndex = 1;
            this.getAdmins();
        },

        // Helper Methods
        getFullImageUrl(pictureUrl) {
            if (!pictureUrl) return null;
            // If the URL is already a full URL (starts with http), return as is
            if (pictureUrl.startsWith("http")) {
                return pictureUrl;
            }
            // Otherwise, prepend the base URL
            return `${this.baseUrl}/${pictureUrl}`;
        },

        // Role helpers
        getRoleColor(role) {
            const colors = {
                [Roles.Admin]: "primary",
                [Roles.SuperAdmin]: "warning",
                [Roles.Administrator]: "info",
            };
            return colors[role] || "grey";
        },

        getRoleIcon(role) {
            const icons = {
                [Roles.Admin]: "mdi-account-supervisor",
                [Roles.SuperAdmin]: "mdi-crown",
                [Roles.Administrator]: "mdi-shield-account",
            };
            return icons[role] || "mdi-account";
        },

        getRoleDisplayName(role) {
            const roleObj = CustomRoles.find((r) => r.Name === role);
            return roleObj ? roleObj.ArCaption : role;
        },

        // Date formatting
        formatDate(date) {
            if (!date) return "";
            return new Date(date).toLocaleDateString("ar-IQ", {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
            });
        },

        formatTime(date) {
            if (!date) return "";
            return new Date(date).toLocaleTimeString("ar-IQ", {
                hour: "2-digit",
                minute: "2-digit",
            });
        },

        // Actions
        viewAdmin(admin) {
            this.selectedAdmin = admin;
            this.adminDetailsDialog = true;
        },

        editAdmin(admin) {
            this.openEditDialog(admin);
        },

        async deleteAdmin(admin) {
            const result = await genericService.swalAlertConfirm({
                title: `هل أنت متأكد من حذف هذا المشرف؟`,
                text: `سيتم حذف المشرف: ${admin.fullName}`,
                confirmButtonText: "نعم، احذف",
                cancelButtonText: "إلغاء",
                icon: "warning",
            });

            if (result.isConfirmed) {
                try {
                    const response = await axios.delete(`/Admin/${admin.id}`);

                    if (response.data.statusCode === 200) {
                        this.$toast.success(`تم حذف المشرف بنجاح`);
                        this.getAdmins(); // Refresh data
                    } else {
                        this.$toast.error("حدث خطأ أثناء حذف المشرف");
                    }
                } catch (error) {
                    console.error("Error deleting admin:", error);
                    if (
                        error.response &&
                        error.response.data &&
                        error.response.data.message
                    ) {
                        this.$toast.error(error.response.data.message);
                    } else {
                        this.$toast.error("حدث خطأ أثناء حذف المشرف");
                    }
                }
            }
        },

        printPage() {
            window.print();
        },
    },
};
</script>

<style scoped>
.shadow {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.cursor-pointer >>> tbody tr {
    cursor: pointer;
}

.cursor-pointer >>> tbody tr:hover {
    background-color: #f5f5f5 !important;
}

#row_colors_dark >>> tbody tr:nth-of-type(even) {
    background-color: rgba(255, 255, 255, 0.05);
}

#row_colors_light >>> tbody tr:nth-of-type(even) {
    background-color: rgba(0, 0, 0, 0.02);
}

.v-data-table >>> .v-data-table__wrapper {
    overflow-x: auto;
}

.required {
    color: red;
    font-weight: bold;
}

.btn {
    text-transform: none !important;
}

@media print {
    .v-btn,
    .v-pagination {
        display: none !important;
    }
}
</style>
